import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { qs } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { Loading, ErrorNote, Empty, Pill, Spinner } from '../components/ui.jsx';
import OnHandPicker from '../components/OnHandPicker.jsx';
import SearchHit from '../components/SearchHit.jsx';

/**
 * Req #12: recipes rankable against what is actually on the shelves.
 *
 * Ranked over the saved recipe index rather than by querying recipe sites.
 * That is the only approach that respects those sites' terms, and it is what
 * makes the ranking instant. The index grows every time a recipe is imported.
 */
export default function CookNow() {
  const navigate = useNavigate();
  const [maxMissing, setMaxMissing] = useState(0);
  const [maxMinutes, setMaxMinutes] = useState('');
  const [picked, setPicked] = useState([]);
  // Null until the button is pressed, and only ever set by pressing it: the
  // web search is metered, so selecting an ingredient must not spend one.
  const [webQuery, setWebQuery] = useState(null);

  const ingredients = picked.map((item) => item.id).join(',');
  const results = useApi(`/recipes/makeable${qs({ maxMissing, maxMinutes, limit: 40, ingredients })}`);
  const sources = useApi('/recipes/sources/list');

  const web = useApi(
    webQuery ? `/recipes/sources/makeable${qs({ ingredients: webQuery, limit: 8 })}` : null,
    { skip: !webQuery },
  );
  // Nothing is shown unless it belongs to the request that is currently
  // settled -- useApi holds the last path's data, and a second search would
  // otherwise render the first one's recipes under its own spinner.
  const webShown = web.loading || web.error ? null : web.data;

  const MISSING_OPTIONS = [
    { value: 0, label: 'Have everything' },
    { value: 1, label: 'Missing 1' },
    { value: 3, label: 'Missing ≤3' },
    { value: 6, label: 'Missing ≤6' },
  ];

  return (
    <div className="stack">
      <div className="card stack">
        <span className="small muted">Cook around what you have</span>
        <OnHandPicker selected={picked} onChange={setPicked} />
      </div>

      <div className="chips scroll-x">
        {MISSING_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`chip ${maxMissing === option.value ? 'active' : ''}`}
            onClick={() => setMaxMissing(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="chips scroll-x">
        {['', '20', '30', '45', '60'].map((minutes) => (
          <button
            key={minutes || 'any'}
            type="button"
            className={`chip ${maxMinutes === minutes ? 'active' : ''}`}
            onClick={() => setMaxMinutes(minutes)}
          >
            {minutes ? `≤${minutes} min` : 'Any time'}
          </button>
        ))}
      </div>

      {results.loading && !results.data && <Loading />}
      <ErrorNote error={results.error} onRetry={results.reload} />

      {results.data?.results?.length === 0 && (
        <Empty title="Nothing matches yet">
          {maxMissing === 0
            ? 'No saved recipe is fully covered by your pantry right now. Try allowing a missing ingredient or two.'
            : 'Import a few more recipes, or stock up.'}
        </Empty>
      )}

      <div className="stack">
        {(results.data?.results || []).map(({ recipe, summary, missing_items: missing, uncertain_items: uncertain }) => (
          <Link
            key={recipe.id}
            className="card stack"
            to={`/recipes/${recipe.id}`}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            <div className="row">
              {recipe.image_url
                ? <img className="thumb" src={recipe.image_url} alt="" loading="lazy" />
                : <div className="thumb" aria-hidden="true" />}
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="truncate" style={{ fontWeight: 580 }}>{recipe.title}</div>
                <div className="tiny muted truncate">
                  {recipe.source_name || 'my recipes'}
                  {recipe.total_minutes ? ` · ${recipe.total_minutes} min` : ''}
                </div>
              </div>
              {summary.can_make
                ? <Pill tone="ok">ready</Pill>
                : <Pill tone={missing.length ? 'warn' : 'unknown'}>
                  {missing.length ? `${missing.length} to buy` : `${uncertain.length} unclear`}
                </Pill>}
            </div>

            {missing.length > 0 && (
              <div className="tiny muted">
                Need: {missing.map((item) => item.name).join(', ')}
              </div>
            )}
            {/* Kept separate from "need": an unverifiable ingredient might well
                be in the cupboard, and lumping it in would misstate the list. */}
            {uncertain.length > 0 && (
              <div className="tiny muted">
                Can&rsquo;t verify: {uncertain.map((item) => item.name).join(', ')}
              </div>
            )}
          </Link>
        ))}
      </div>

      <section className="stack">
        <div className="section-title"><h2>From the web</h2></div>

        {picked.length === 0 && (
          <p className="small muted" style={{ margin: 0 }}>
            Pick one to three ingredients above to search your sources.
          </p>
        )}

        {/* Pressed, never automatic. Each press is one metered search, so a
            change of mind about an ingredient must not spend one. Commas are
            stripped from the names before joining: these are search terms, and
            "Tomatoes, Diced" as one pick must not arrive as two ingredients. */}
        <button
          type="button"
          className="primary full"
          disabled={picked.length === 0 || web.loading}
          onClick={() => setWebQuery(picked.map((item) => item.name.replace(/,/g, ' ').trim()).join(','))}
        >
          Search the web with these
        </button>

        {/* The server fetches and reads up to eight pages before it answers.
            Saying what it is doing is the difference between a slow screen and
            a broken one. */}
        {web.loading && (
          <p className="small muted" style={{ margin: 0 }}>
            <Spinner /> Checking each recipe against your pantry…
          </p>
        )}

        {/* With a retry: pressing the button again sets the same webQuery,
            which React bails out on, so without this a transient 429 or 502
            would leave the section permanently inert. */}
        <ErrorNote error={web.error} onRetry={web.reload} />

        {/* Search being unconfigured is the one failure with a way round it:
            the sources are still readable by hand, so the section offers them
            rather than dead-ending on a setting only an admin can change. */}
        {web.error?.code === 'RECIPE_SEARCH_NOT_CONFIGURED' && sources.data?.sources?.length > 0 && (
          <>
            <p className="small muted" style={{ margin: 0 }}>
              Ranking runs over recipes you&rsquo;ve saved. To widen the net, search one of your curated
              sources and import what you like — the link importer reads the site&rsquo;s own recipe data.
            </p>
            <div className="chips">
              {sources.data.sources.filter((source) => source.enabled).map((source) => (
                <a
                  key={source.id}
                  className="chip"
                  href={source.search_url_template
                    ? source.search_url_template.replace('{query}', 'dinner')
                    : `https://${source.domain}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {source.name} ↗
                </a>
              ))}
            </div>
          </>
        )}

        {/* Named, because the results outlive the selection that produced
            them: the chips above may have changed since this search ran. */}
        {webShown && (
          <p className="tiny muted" style={{ margin: 0 }}>
            Results for: {webShown.ingredients?.join(', ') || webShown.query}
          </p>
        )}

        {webShown?.results?.length === 0 && (
          <p className="small muted" style={{ margin: 0 }}>
            Nothing came back for those ingredients. Try one fewer, or a different pair.
          </p>
        )}

        {/* A result that already came out of a roundup arrives here at the top
            level -- the preview puts a page's children in its place in line --
            and it carries the list it came from. One level is the whole rule,
            so that is the same no here as it is inside an expansion. */}
        {(webShown?.results || []).map((hit) => (
          <SearchHit
            key={hit.url}
            hit={hit}
            onImported={(recipe) => navigate(`/recipes/${recipe.id}`)}
            canExpand={!hit.from_collection}
          />
        ))}
      </section>
    </div>
  );
}
